import {isNumber, isString} from 'lodash';

import {ReferenceFormatModel} from '../../models/api/autolinkerModel';
import {RegexCharacterClassesEnum, RegexPart, StringOrTemplate} from '../editors/commons/stringTemplateModel';
import {ExpressionRef, isExpressionRef} from '../editors/expressions/expressionRefModel';

const characterClassMap: Record<RegexCharacterClassesEnum, string> = {
  digit: '\\d',
  alpha: '[a-zA-Z]',
  alpha_digit: '[a-zA-Z\\d]',
  word: '\\w',
  printable: '\\S',
  any: '.',
  word_boundary: '\\b',
};

export function compileExtractRegexPattern(format: ReferenceFormatModel): string {
  const {pattern, before, after} = format;

  const patternSource = compileRegexPattern(pattern);
  const tokens: string[] = [];

  // Start with preceding pattern.
  // Use a look-behind assertion so we don't capture it
  if (before) {
    const beforeSource = compileRegexPattern(before);
    tokens.push('(?<=');
    tokens.push(beforeSource);
    tokens.push('\\s*');
    tokens.push(')');
  }

  // Main content.
  tokens.push(patternSource);

  // End with following pattern.
  // Use a look-ahead assertion so we don't capture it
  if (after) {
    const afterSource = compileRegexPattern(after);
    tokens.push('(?=');
    tokens.push('\\s*');
    tokens.push(afterSource);
    tokens.push(')');
  }

  const source = tokens.join('');

  return source;
}

export interface RegexMatch {
  value: string;
  index: number;
}

export function matchFormat(
  format: ReferenceFormatModel,
  text: string,
  shouldOnlyMatchFirst?: boolean,
): RegexMatch[] {
  const source = compileExtractRegexPattern(format);
  const regex = new RegExp(source, 'ig');

  const matches: RegexMatch[] = [];

  let match: RegExpExecArray | null = null;

  // eslint-disable-next-line no-cond-assign
  while ((match = regex.exec(text))) {
    // Before there can be a "preceded by:" assertion, extract the 1st group and find out its index.
    const [extractedText] = match;

    matches.push(
      trimMatch({
        value: extractedText,
        index: match.index,
      }),
    );

    if (shouldOnlyMatchFirst) {
      break;
    }
  }

  return matches;
}

function compileRegexPattern(pattern: StringOrTemplate) {
  if (isString(pattern)) {
    return compileRegexPart(pattern);
  }

  return pattern.template.map(compileRegexPart).join('');
}

function compileRegexPart(part: string | RegexPart | ExpressionRef) {
  // If the regex references a variable, ignore it.
  if (isExpressionRef(part)) {
    return '';
  }

  if (isString(part)) {
    return conformStringForRegex(part);
  }

  const source = characterClassMap[part.char];
  const repeat = compileRegexRepeat(part);

  return source + repeat;
}

function compileRegexRepeat(part: RegexPart): string {
  if (part.char === RegexCharacterClassesEnum.WORD_BOUNDARY) {
    return '';
  }

  return isNumber(part.repeat) ? `{${part.repeat}}` : '+';
}

function escapeStringRegexp(value: string) {
  // Escape characters with special meaning either inside or outside character sets.
  // Use a simple backslash escape when it’s always valid, and a `\xnn` escape when the simpler form would be disallowed by Unicode patterns’ stricter grammar.
  return value.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&').replace(/-/g, '\\x2d');
}

function conformStringForRegex(value: string) {
  if (!value) {
    return '';
  }

  return escapeStringRegexp(value.toLowerCase().replace(/\s+/g, ' '));
}

function trimMatch(match: RegexMatch): RegexMatch {
  return trimRight(trimLeft(match));
}

function trimLeft(match: RegexMatch): RegexMatch {
  const leftSpaceMatch = /^\s+/.exec(match.value);

  if (!leftSpaceMatch) {
    return match;
  }

  const leftSpace = leftSpaceMatch[0];

  return {
    value: match.value.slice(leftSpace.length),
    index: match.index + leftSpace.length,
  };
}

function trimRight(match: RegexMatch): RegexMatch {
  const rightSpaceMatch = /\s+$/.exec(match.value);

  if (!rightSpaceMatch) {
    return match;
  }

  const rightSpace = rightSpaceMatch[0];

  return {
    value: match.value.slice(0, -rightSpace.length),
    index: match.index,
  };
}
